import axios from "axios";

export const adminFetchOrdersApi = async (
  token,
  signal,
  pageNr = null,
  sortKey = null,
  filterKey = null
) => {
  // get request to the server to fetch all orders
  // token (String): bearer token used for authentication
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  // pageNr (Number): the new page nr requested by the user (pagination)
  // sortKey (String): the attribute to be used for sorting, e.g. "date_1"
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    // set query parameters
    let queryParams = "";
    if (pageNr) {
      queryParams = `?pageNr=${pageNr}`;
    }
    if (sortKey) {
      queryParams = queryParams ? queryParams + "&" : "?";
      queryParams = queryParams + `sortKey=${sortKey}`;
    }
    if (filterKey) {
      queryParams = queryParams ? queryParams + "&" : "?";
      queryParams = queryParams + `filterKey=${filterKey}`;
    }
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/orders/admin${queryParams}`,
      config,
      { signal }
    );
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const adminFetchUserOrdersApi = async (
  token,
  userId,
  signal,
  pageNr = null
) => {
  // get request to the server to fetch all orders for a specified user
  // token (String): bearer token used for authentication
  // userId (String): the id of the user whose orders will be fetched
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  // pageNr (Number): the new page nr requested by the user (pagination)
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    // set query parameters
    let queryParams = "";
    if (pageNr) {
      queryParams = `?pageNr=${pageNr}`;
    }
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/orders/admin/user/${userId}${queryParams}`,
      config,
      { signal }
    );
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const adminFetchOrderDetailsApi = async (token, orderId, signal) => {
  // get request to the server to fetch the details of a specific order
  // token (String): bearer token used for authentication
  // orderId (String): the id of the order to be fetched
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/orders/admin/${orderId}`, config, {
      signal,
    });
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const adminSetCancellationStatusApi = async (
  token,
  orderId,
  cancellationStatus,
  signal
) => {
  // put request to the server to update the cancellation status of an order
  // token (String): bearer token used for authentication
  // orderId (String): the id of the order to be updated
  // cancellationStatus (Boolean): true if order is to be cancelled, false otherwise
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.put(
      `${process.env.REACT_APP_API_URL}/api/orders/${orderId}/cancelled`,
      {
        status: cancellationStatus,
      },
      config,
      { signal }
    );
    // returns update status as json: {status:(Boolean), date:(Date)}
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const adminSetDeliveryStatusApi = async (
  token,
  orderId,
  deliveryStatus,
  signal
) => {
    // put request to the server to update the delivery status of an order
  // token (String): bearer token used for authentication
  // orderId (String): the id of the order to be updated
  // deliveryStatus (Boolean): true if the status is to be changed to delivered, false otherwise
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.put(
      `${process.env.REACT_APP_API_URL}/api/orders/${orderId}/delivered`,
      {
        status: deliveryStatus,
      },
      config,
      { signal }
    );
    // returns update status as json: {status:(Boolean), date:(Date)}
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const adminSetReturnedStatusApi = async (
  token,
  orderId,
  returnedStatus,
  signal
) => {
      // put request to the server to update the returned status of an order
  // token (String): bearer token used for authentication
  // orderId (String): the id of the order to be updated
  // returnedStatus (Boolean): true if the status is to be changed to returned, false otherwise
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.put(
      `${process.env.REACT_APP_API_URL}/api/orders/${orderId}/returned`,
      {
        status: returnedStatus,
      },
      config,
      { signal }
    );
    // returns update status as json: {status:(Boolean), date:(Date)}
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const adminSetRefundedStatusApi = async (
  token,
  orderId,
  refundedStatus,
  signal
) => {
      // put request to the server to update the refunded status of an order
  // token (String): bearer token used for authentication
  // orderId (String): the id of the order to be updated
  // refundedStatus (Boolean): true if the status is to be changed to refunded, false otherwise
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.put(
      `${process.env.REACT_APP_API_URL}/api/orders/${orderId}/refunded`,
      {
        status: refundedStatus,
      },
      config,
      { signal }
    );
    // returns update status as json: {status:(Boolean), date:(Date)}
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const fetchMonthlyOrdersApi = async (token, date1, date2) => {
      // get request to the server to fetch all orders placed between date1 and date2
  // token (String): bearer token used for authentication
  // date1 (String): the start date, format 2023-08-13
  // date2 (String): the end date, format 2023-08-13
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const url = `${process.env.REACT_APP_API_URL}/api/orders/analytics/monthly-sales?start=${date1}&end=${date2}`;
    const { data } = await axios.get(url, config);
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};


export const fetchOrdersApi = async (
  token,
  signal,
  pageNr = null,
  sortKey = false,
  filterKey = false
) => {
      // get request to the server to fetch the requesting user's orders
      // token (String): bearer token used for authentication
      // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
        // pageNr (Number): the new page nr requested by the user (pagination)
// sortKey (String): the attribute to sort on, eg "date_1"
// filterKey (String): the attribute to filter on, eg "paid_1"
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    let queryParams = "";
    if (pageNr) {
      queryParams = `?pageNr=${pageNr}`;
    }
    if (sortKey) {
      queryParams = queryParams ? queryParams + "&" : "?";
      queryParams = queryParams + `sortKey=${sortKey}`;
    }
    if (filterKey) {
      queryParams = queryParams ? queryParams + "&" : "?";
      queryParams = queryParams + `filterKey=${filterKey}`;
    }

    const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/orders${queryParams}`, config, {
      signal,
    });
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const fetchOrderDetailsApi = async (token, orderId, signal) => {
        // get request to the server to fetch the details for a specific order
      // token (String): bearer token used for authentication
      // orderId(String): the id of the requested order 
      // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/orders/${orderId}`, config, {
      signal,
    });
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const createOrderApi = async (token, orderData, signal) => {
        // post request to the server to create a new order
      // token (String): bearer token used for authentication
      // orderData (JSON): the data for the new order
      // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/api/orders/`, orderData, config, {
      signal,
    });
    // returns update status as json: {status:(Boolean), date:(Date)}
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      // console.log("err here", error)
      throw newErr;
    }
  }
};
